import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Blocks, Check, Database, LockKeyhole, Workflow, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { BRAND } from "../brand";
import { Button } from "../components/ui";
import { DemoLoginHint } from "../components/DemoLoginHint";

interface TelegramAuthUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramAuthUser) => void;
  }
}

interface TelegramConfig {
  enabled: boolean;
  botUsername?: string;
}

function TelegramLoginButton({ botUsername, onAuth }: { botUsername: string; onAuth: (user: TelegramAuthUser) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const authRef = useRef(onAuth);
  authRef.current = onAuth;
  useEffect(() => {
    const host = container.current;
    if (!host) return;
    window.onTelegramAuth = (user) => authRef.current(user);
    const script = document.createElement("script");
    // The widget is allowed only by the /login document CSP (public/_headers). After an in-app
    // redirect the document still carries the site-wide CSP, so reload /login once as a document.
    const onViolation = (event: SecurityPolicyViolationEvent) => {
      if (!event.blockedURI.startsWith("https://telegram.org/")) return;
      if (sessionStorage.getItem("telegram-csp-reload") === "1") return;
      sessionStorage.setItem("telegram-csp-reload", "1");
      window.location.replace("/login");
    };
    document.addEventListener("securitypolicyviolation", onViolation);
    script.onload = () => sessionStorage.removeItem("telegram-csp-reload");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.dataset.telegramLogin = botUsername;
    script.dataset.size = "large";
    script.dataset.onauth = "onTelegramAuth(user)";
    host.appendChild(script);
    return () => {
      document.removeEventListener("securitypolicyviolation", onViolation);
      script.remove();
      host.replaceChildren();
      if (window.onTelegramAuth) delete window.onTelegramAuth;
    };
  }, [botUsername]);
  return <div className="telegram-login" ref={container} />;
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const onSignedIn = async () => {
    await client.invalidateQueries({ queryKey: ["session"] });
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    navigate(from, { replace: true });
  };
  const login = useMutation({
    mutationFn: () => api("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
    onSuccess: onSignedIn,
  });
  const telegramConfig = useQuery({
    queryKey: ["telegram-config"],
    queryFn: () => api<TelegramConfig>("/api/auth/telegram/config"),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const telegramLogin = useMutation({
    mutationFn: (user: TelegramAuthUser) => api("/api/auth/telegram", { method: "POST", body: JSON.stringify(user) }),
    onSuccess: onSignedIn,
  });
  const telegramEnabled = telegramConfig.data?.enabled === true && Boolean(telegramConfig.data?.botUsername);
  return <div className="login-page">
    <section className="login-story">
      <div className="login-brand"><span><Zap fill="currentColor" /></span>{BRAND.name}</div>
      <div className="login-copy"><div className="eyebrow">Customer operating system</div><h1>One relationship layer.<br /><em>Every industry.</em></h1><p>Model your business without forcing it into somebody else's vocabulary. Configure objects, pipelines and automations as you grow.</p><ul><li><Check /> Universal object and relationship model</li><li><Check /> Event-driven automation with durable audit</li><li><Check /> Cloudflare-native security and performance</li></ul></div>
      <div className="login-architecture"><div><Database /><span><b>Data</b>D1 + R2</span></div><i /><div><Workflow /><span><b>Events</b>Queue + Outbox</span></div><i /><div><Blocks /><span><b>Modules</b>Capability driven</span></div></div>
    </section>
    <section className="login-panel"><form onSubmit={(event) => { event.preventDefault(); login.mutate(); }}><div className="login-icon"><LockKeyhole /></div><h2>Welcome back</h2><p>Sign in to your private CRM workspace.</p><DemoLoginHint onSignedIn={onSignedIn} /><label>Email address<input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required autoFocus /></label>{login.error && <div className="form-error">{login.error.message}</div>}<Button type="submit" disabled={login.isPending}>{login.isPending ? "Signing in…" : <>Open workspace <ArrowRight size={17} /></>}</Button>{telegramEnabled && <><div className="login-divider"><span>or</span></div><TelegramLoginButton botUsername={telegramConfig.data!.botUsername!} onAuth={(user) => telegramLogin.mutate(user)} />{telegramLogin.error && <div className="form-error">{telegramLogin.error.message}</div>}</>}<div className="login-security"><LockKeyhole size={14} /> Encrypted session · 12-hour expiry · Owner-only bootstrap</div></form></section>
  </div>;
}
